import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { departmentsService } from '../../../services/supabaseService';

const DepartmentSettingsSection = () => {
  const defaultDepartments = [
    {
      id: 1,
      name: 'Engineering',
      manager: 'Sarah Johnson',
      customThreshold: true,
      warningLevel: 6,
      terminationLevel: 18,
      approvalWorkflow: 'Manager → Director → HR',
      inheritGlobal: false
    },
    {
      id: 2,
      name: 'Operations',
      manager: 'Michael Chen',
      customThreshold: true,
      warningLevel: 4,
      terminationLevel: 15,
      approvalWorkflow: 'Manager → HR',
      inheritGlobal: false
    },
    {
      id: 3,
      name: 'Sales',
      manager: 'Emily Rodriguez',
      customThreshold: false,
      warningLevel: 5,
      terminationLevel: 20,
      approvalWorkflow: 'Manager → HR',
      inheritGlobal: true
    },
    {
      id: 4,
      name: 'Marketing',
      manager: 'David Park',
      customThreshold: false,
      warningLevel: 5,
      terminationLevel: 20,
      approvalWorkflow: 'Manager → HR',
      inheritGlobal: true
    }
  ];

  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    manager: '',
    customThreshold: false,
    warningLevel: 5,
    terminationLevel: 20,
    approvalWorkflow: '',
    inheritGlobal: true
  });

  // Load departments from Supabase on component mount
  useEffect(() => {
    loadDepartments();
  }, []);

  const loadDepartments = async () => {
    try {
      setLoading(true);
      const data = await departmentsService?.getAll();
      setDepartments(data);
    } catch (error) {
      console.error('Error loading departments:', error);
    } finally {
      setLoading(false);
    }
  };

  const workflowOptions = [
    { value: 'Manager → HR', label: 'Manager → HR' },
    { value: 'Manager → Director → HR', label: 'Manager → Director → HR' },
    { value: 'Manager → VP → HR', label: 'Manager → VP → HR' },
    { value: 'Direct to HR', label: 'Direct to HR' }
  ];

  const handleEdit = (dept) => {
    setEditingId(dept?.id);
    setIsAdding(false);
    setFormData({
      name: dept?.name,
      manager: dept?.manager,
      customThreshold: dept?.customThreshold,
      warningLevel: dept?.warningLevel,
      terminationLevel: dept?.terminationLevel,
      approvalWorkflow: dept?.approvalWorkflow,
      inheritGlobal: dept?.inheritGlobal
    });
  };

  const handleAdd = () => {
    setIsAdding(true);
    setEditingId(null);
    setFormData({
      name: '',
      manager: '',
      customThreshold: false,
      warningLevel: 5,
      terminationLevel: 20,
      approvalWorkflow: 'Manager → HR',
      inheritGlobal: true
    });
  };

  const handleSave = async () => {
    try {
      if (isAdding) {
        const newDept = await departmentsService?.create(formData);
        if (newDept) {
          setDepartments(prev => [...prev, newDept]);
          setIsAdding(false);
        }
      } else {
        const updatedDept = await departmentsService?.update(editingId, formData);
        if (updatedDept) {
          setDepartments(prev => prev?.map(d => d?.id === editingId ? updatedDept : d));
          setEditingId(null);
        }
      }
      setFormData({
        name: '',
        manager: '',
        customThreshold: false,
        warningLevel: 5,
        terminationLevel: 20,
        approvalWorkflow: '',
        inheritGlobal: true
      });
    } catch (error) {
      console.error('Error saving department:', error);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setIsAdding(false);
    setFormData({
      name: '',
      manager: '',
      customThreshold: false,
      warningLevel: 5,
      terminationLevel: 20,
      approvalWorkflow: '',
      inheritGlobal: true
    });
  };

  const handleDelete = (id) => {
    setDeletingId(id);
  };

  const confirmDelete = async () => {
    try {
      const success = await departmentsService?.delete(deletingId);
      if (success) {
        setDepartments(prev => prev?.filter(d => d?.id !== deletingId));
      }
      setDeletingId(null);
    } catch (error) {
      console.error('Error deleting department:', error);
      setDeletingId(null);
    }
  };

  const cancelDelete = () => {
    setDeletingId(null);
  };

  return (
    <div className="p-6">
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-sm text-muted-foreground">Loading departments...</p>
          </div>
        </div>
      ) : (
        <>
          <div className="mb-6 flex items-start justify-between">
            <div>
              <h2 className="font-heading font-semibold text-xl text-foreground mb-1">
                Department Settings
              </h2>
              <p className="font-body text-sm text-muted-foreground">
                Per-department customization of compliance standards, reporting hierarchies, and approval workflows
              </p>
            </div>
            <Button
              variant="default"
              size="sm"
              iconName="Plus"
              onClick={handleAdd}
              disabled={isAdding || editingId !== null}
            >
              Add Department
            </Button>
          </div>

          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} className="text-primary mt-0.5" />
              <div>
                <h4 className="font-heading font-semibold text-sm text-foreground mb-1">
                  Global Default Settings
                </h4>
                <div className="text-sm text-muted-foreground space-y-1">
                  <p>• Warning Level: <span className="font-semibold text-foreground">5 points</span></p>
                  <p>• Termination Level: <span className="font-semibold text-foreground">20 points</span></p>
                  <p>• Default Workflow: <span className="font-semibold text-foreground">Manager → HR</span></p>
                  <p className="text-xs mt-2 italic">Departments can inherit these settings or customize their own thresholds</p>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {isAdding && (
              <div className="bg-card border-2 border-primary rounded-lg p-4">
                <div className="mb-4">
                  <h3 className="font-heading font-semibold text-lg text-foreground mb-2">
                    Add New Department
                  </h3>
                </div>
                <div className="space-y-4 mb-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Department Name"
                      value={formData?.name}
                      onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
                      placeholder="Enter department name"
                    />
                    <Input
                      label="Department Manager"
                      value={formData?.manager}
                      onChange={(e) => setFormData({ ...formData, manager: e?.target?.value })}
                      placeholder="Enter manager name"
                    />
                  </div>

                  <Checkbox
                    label="Inherit global settings"
                    description="Use system-wide default thresholds and workflows"
                    checked={formData?.inheritGlobal}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      inheritGlobal: e?.target?.checked,
                      customThreshold: !e?.target?.checked
                    })}
                  />

                  {!formData?.inheritGlobal && (
                    <div className="bg-muted/30 rounded-lg p-4 space-y-4">
                      <h4 className="font-heading font-semibold text-sm text-foreground">
                        Custom Department Thresholds
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          label="Warning Level (Points)"
                          type="number"
                          min="0"
                          value={formData?.warningLevel}
                          onChange={(e) => setFormData({ ...formData, warningLevel: parseInt(e?.target?.value) })}
                        />
                        <Input
                          label="Termination Level (Points)"
                          type="number"
                          min="0"
                          value={formData?.terminationLevel}
                          onChange={(e) => setFormData({ ...formData, terminationLevel: parseInt(e?.target?.value) })}
                        />
                      </div>
                      <Select
                        label="Approval Workflow"
                        options={workflowOptions}
                        value={formData?.approvalWorkflow}
                        onChange={(value) => setFormData({ ...formData, approvalWorkflow: value })}
                      />
                    </div>
                  )}
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={handleCancel}>
                    Cancel
                  </Button>
                  <Button 
                    variant="default" 
                    size="sm" 
                    onClick={handleSave}
                    disabled={!formData?.name?.trim() || !formData?.manager?.trim()}
                  >
                    Add Department
                  </Button>
                </div>
              </div>
            )}

            {departments?.map((dept) => (
              <div
                key={dept?.id}
                className="bg-card border border-border rounded-lg p-4"
              >
                {editingId === dept?.id ? (
                  <div>
                    <div className="space-y-4 mb-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          label="Department Name"
                          value={formData?.name}
                          onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
                        />
                        <Input
                          label="Department Manager"
                          value={formData?.manager}
                          onChange={(e) => setFormData({ ...formData, manager: e?.target?.value })}
                        />
                      </div>

                      <Checkbox
                        label="Inherit global settings"
                        description="Use system-wide default thresholds and workflows"
                        checked={formData?.inheritGlobal}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          inheritGlobal: e?.target?.checked,
                          customThreshold: !e?.target?.checked
                        })}
                      />

                      {!formData?.inheritGlobal && (
                        <div className="bg-muted/30 rounded-lg p-4 space-y-4">
                          <h4 className="font-heading font-semibold text-sm text-foreground">
                            Custom Department Thresholds
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Input
                              label="Warning Level (Points)"
                              type="number"
                              min="0"
                              value={formData?.warningLevel}
                              onChange={(e) => setFormData({ ...formData, warningLevel: parseInt(e?.target?.value) })}
                            />
                            <Input
                              label="Termination Level (Points)"
                              type="number"
                              min="0"
                              value={formData?.terminationLevel}
                              onChange={(e) => setFormData({ ...formData, terminationLevel: parseInt(e?.target?.value) })}
                            />
                          </div>
                          <Select
                            label="Approval Workflow"
                            options={workflowOptions}
                            value={formData?.approvalWorkflow}
                            onChange={(value) => setFormData({ ...formData, approvalWorkflow: value })}
                          />
                        </div>
                      )}
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" size="sm" onClick={handleCancel}>
                        Cancel
                      </Button>
                      <Button variant="default" size="sm" onClick={handleSave}>
                        Save Changes
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10">
                          <Icon name="Building" size={24} className="text-primary" />
                        </div>
                        <div>
                          <h4 className="font-heading font-semibold text-lg text-foreground">
                            {dept?.name}
                          </h4>
                          <p className="text-sm text-muted-foreground">
                            Manager: {dept?.manager}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Edit2"
                          onClick={() => handleEdit(dept)}
                          disabled={isAdding || editingId !== null}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Trash2"
                          onClick={() => handleDelete(dept?.id)}
                          disabled={isAdding || editingId !== null}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-muted/30 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <Icon name="AlertCircle" size={16} className="text-warning" />
                          <span className="text-xs font-semibold text-muted-foreground">Warning Level</span>
                        </div>
                        <p className="font-data font-semibold text-lg text-foreground">
                          {dept?.warningLevel} points
                        </p>
                      </div>

                      <div className="bg-muted/30 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <Icon name="XCircle" size={16} className="text-destructive" />
                          <span className="text-xs font-semibold text-muted-foreground">Termination Level</span>
                        </div>
                        <p className="font-data font-semibold text-lg text-foreground">
                          {dept?.terminationLevel} points
                        </p>
                      </div>

                      <div className="bg-muted/30 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <Icon name="GitBranch" size={16} className="text-primary" />
                          <span className="text-xs font-semibold text-muted-foreground">Approval Flow</span>
                        </div>
                        <p className="text-sm text-foreground">
                          {dept?.approvalWorkflow}
                        </p>
                      </div>
                    </div>

                    <div className="mt-3">
                      {dept?.inheritGlobal ? (
                        <div className="flex items-center gap-2 text-sm text-success">
                          <Icon name="Check" size={16} />
                          <span>Using global default settings</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-sm text-primary">
                          <Icon name="Settings" size={16} />
                          <span>Custom department settings active</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Delete Confirmation Dialog */}
          {deletingId && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-card rounded-lg p-6 max-w-md w-full mx-4 elevation-lg">
                <div className="flex items-start gap-3 mb-4">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-destructive/10">
                    <Icon name="AlertTriangle" size={24} className="text-destructive" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading font-semibold text-lg text-foreground mb-1">
                      Delete Department
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Are you sure you want to delete <span className="font-semibold text-foreground">{departments?.find(d => d?.id === deletingId)?.name}</span>? This action cannot be undone.
                    </p>
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={cancelDelete}>
                    Cancel
                  </Button>
                  <Button variant="destructive" size="sm" onClick={confirmDelete}>
                    Delete Department
                  </Button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DepartmentSettingsSection;